import { useEffect, useState } from 'react';

import { Box, Button, Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

import { ReactComponent as DeleteIcon } from 'assets/images/icons/icn_table_cell_Bin.svg';

import ModalContainer from 'components/ModalContainer';
import { MenuSelect } from 'components/Select/menuSelect';
import { SolidButton as SolidRectangleButton } from 'components/buttons/SolidRectangleButton';
import { OutlineRectangleButton } from 'components/buttons/OutlineRectangleButton';

import { ALL_LANGUAGE_OPTIONS, DEFAULT_LANGUAGE_CONFIG } from '../const';

const MultipleLanguagesModal = ({ languageConfig, open, onClose, onSave }) => {
  const theme = useTheme();
  const [draft, setDraft] = useState(languageConfig);
  const [languageToAdd, setLanguageToAdd] = useState('cs-CZ');

  const sectionLabelSx = {
    fontSize: theme.typography.customFontSize[2],
    color: theme.palette.primary.greyPrimary,
    marginBottom: '8px',
  };

  useEffect(() => {
    if (open) {
      setDraft(languageConfig);
      const available = ALL_LANGUAGE_OPTIONS.find(option => !languageConfig.supportLanguages.some(lang => lang.value === option.value));
      setLanguageToAdd(available?.value || 'cs-CZ');
    }
  }, [open, languageConfig]);

  const addableOptions = ALL_LANGUAGE_OPTIONS.filter(option => !draft.supportLanguages.some(lang => lang.value === option.value));

  const handleAddLanguage = () => {
    const selected = ALL_LANGUAGE_OPTIONS.find(option => option.value === languageToAdd);
    if (!selected) {
      return;
    }

    setDraft(prev => ({
      ...prev,
      supportLanguages: [...prev.supportLanguages, selected],
    }));

    const nextAvailable = addableOptions.find(option => option.value !== languageToAdd);
    if (nextAvailable) {
      setLanguageToAdd(nextAvailable.value);
    }
  };

  const handleRemoveLanguage = value => {
    if (value === draft.defaultLanguage || value === DEFAULT_LANGUAGE_CONFIG.defaultLanguage) {
      return;
    }

    setDraft(prev => ({
      ...prev,
      supportLanguages: prev.supportLanguages.filter(lang => lang.value !== value),
    }));
  };

  const handleConfirm = () => {
    onSave(draft);
    onClose();
  };

  const selectWidth = '100%';

  return (
    <ModalContainer display={open} modalWidth="600px">
      <Box sx={{ padding: '0 10px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
            }}
          >
            Multiple Languages
          </Typography>
          <Button
            onClick={onClose}
            sx={{
              minWidth: 'auto',
              padding: 0,
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>

        <Divider sx={{ marginBottom: '16px', borderColor: theme.palette.primary.borderGreyPrimary }} />

        <Box sx={{ marginTop: '16px', marginBottom: '12px' }}>
          <Typography sx={sectionLabelSx}>Default Language</Typography>
          <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <MenuSelect
              width={selectWidth}
              value={draft.defaultLanguage}
              options={draft.supportLanguages}
              onChange={obj =>
                setDraft(prev => ({
                  ...prev,
                  defaultLanguage: obj.value,
                }))
              }
            />
            <Box width="100px" />
          </Box>
        </Box>

        <Box sx={{ marginBottom: '16px' }}>
          <Typography sx={sectionLabelSx}>Add Support Language</Typography>
          <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <MenuSelect width={selectWidth} value={languageToAdd} options={addableOptions} onChange={obj => setLanguageToAdd(obj.value)} disabled={addableOptions.length === 0} />
            </Box>
            <SolidRectangleButton width="80px" height="30px" onClick={handleAddLanguage} disabled={addableOptions.length === 0}>
              Add
            </SolidRectangleButton>
          </Box>
        </Box>

        <Divider sx={{ marginBottom: '16px', borderColor: theme.palette.primary.borderGreyPrimary }} />

        <Typography
          sx={{
            fontSize: theme.typography.customFontSize[2],
            color: theme.palette.primary.greyPrimary,
            fontWeight: theme.typography.customFontWeight[1],
            marginBottom: '12px',
          }}
        >
          Support Languages
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px',
            marginBottom: '24px',
          }}
        >
          {draft.supportLanguages.map(language => {
            const isDefault = language.value === draft.defaultLanguage;

            return (
              <Box
                key={language.value}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
                  borderRadius: theme.shape.customBorderRadius[1],
                  padding: '10px 12px',
                  minHeight: 44,
                  backgroundColor: theme.palette.primary.white,
                }}
              >
                {!isDefault && language.value !== DEFAULT_LANGUAGE_CONFIG.defaultLanguage && (
                  <Button
                    onClick={() => handleRemoveLanguage(language.value)}
                    sx={{
                      minWidth: 'auto',
                      padding: 0,
                      backgroundColor: theme.palette.primary.transparent,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.transparent,
                      },
                    }}
                  >
                    <DeleteIcon width="18" height="18" />
                  </Button>
                )}
                <Typography
                  sx={{
                    fontSize: theme.typography.customFontSize[2],
                    color: theme.palette.primary.greyPrimary,
                  }}
                >
                  {language.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <OutlineRectangleButton width="80px" height="32px" onClick={onClose}>
            No
          </OutlineRectangleButton>
          <SolidRectangleButton width="80px" height="32px" onClick={handleConfirm}>
            Yes
          </SolidRectangleButton>
        </Box>
      </Box>
    </ModalContainer>
  );
};

export default MultipleLanguagesModal;
